import { Box, Button, Divider, Typography } from '@mui/material';
import { ContentCard, Dropdown, ProgressCircle } from '../UI';
import { QuestionType } from '../../Utilis/Types/Task';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { InputType } from '../UI/DynamicInput/types';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DynamicInput } from '../UI/DynamicInput';
import { QuestionInputProps } from './types';
import { Check, Close, EditOutlined } from '@mui/icons-material';
import { styles } from './styles';
import { useAppSelector } from '../../store/hooks';

const phoneNumberUtil = PhoneNumberUtil.getInstance();

const getSchema = (inputs: { name: string; type: InputType }[]) => {
  const types: any = {};

  for (const input of inputs) {
    let validation;

    switch (input.type) {
      case InputType.TEXTAREA:
      case InputType.INPUT:
        validation = z.string().min(1);
        break;
      case InputType.SSN:
        validation = z.string().regex(new RegExp(/^\d{3}-\d{2}-\d{4}$/));
        break;
      case InputType.EIN:
        validation = z.string().regex(new RegExp(/^\d{2}-\d{7}$/));
        break;
      case InputType.NUMBER:
      case InputType.VALUE:
        validation = z.number().min(1);
        break;
      case InputType.BOOLEAN:
        validation = z.boolean({
          required_error: 'Select one of the options',
        });
        break;
      case InputType.STATES:
      case InputType.COUNTRIES:
      case InputType.SELECT:
        validation = z
          .string({ required_error: 'Select one of the options' })
          .min(1);
        break;
      case InputType.MULTISELECT:
        validation = z
          .array(z.any(), { required_error: 'Select one of the options' })
          .min(1);
        break;
      case InputType.DATE:
        validation = z.coerce.date();
        break;
      case InputType.PHONE_NUMBER:
        validation = z
          .string({ required_error: 'Phone number is required' })
          .refine(value => {
            try {
              const parsedNumber = phoneNumberUtil.parse(value);
              const isValidNumber =
                !!phoneNumberUtil.isValidNumber(parsedNumber);
              return isValidNumber;
            } catch {
              return false;
            }
          }, 'Invalid phone number');
        break;
      case InputType.EMAIL:
        validation = z
          .string({ required_error: 'Email is required' })
          .refine(
            value => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value),
            'Invalid email address'
          );
        break;
      default:
        validation = undefined;
        break;
    }

    types[input.name] = validation;
  }

  return z.object(types);
};

const formatDefaultValue = (type: InputType, value: any) => {
  if (value === undefined) {
    if (type === InputType.MULTISELECT) {
      return [];
    } else {
      return '';
    }
  } else {
    return value;
  }
};

const getQuestions = (question: any, values: any) => {
  if (question.type === QuestionType.ADDRESS) {
    return [
      {
        title: 'Street Address',
        name: 'streetAddress',
        type: InputType.INPUT,
        placeholder: 'Street Address',
      },
      {
        title: 'City',
        name: 'city',
        type: InputType.INPUT,
        placeholder: 'City',
      },
      {
        title: 'State',
        name: 'state',
        type: InputType.STATES,
        placeholder: 'State',
      },
      {
        title: 'Zip',
        name: 'zip',
        type: InputType.INPUT,
        placeholder: 'Zip',
      },
    ];
  } else if (question.type === QuestionType.SSN_EIN) {
    if (values?.type === QuestionType.SSN) {
      return [
        {
          title: 'SSN',
          name: 'ssn',
          type: InputType.SSN,
          placeholder: 'Enter SSN',
        },
      ];
    } else {
      return [
        {
          title: 'EIN',
          name: 'ein',
          type: InputType.EIN,
          placeholder: 'Enter EIN',
        },
        {
          title: 'Financial Institution',
          name: 'financialInstitution',
          type: InputType.INPUT,
          placeholder: 'Enter Financial Institution',
        },
      ];
    }
  } else {
    const { title, ...rest } = question;

    return [{ ...rest, name: 'test' }];
  }
};

export const QuestionInput = ({
  title,
  question,
  boxShadow = true,
  isLastQuestion,
  onSubmit,
}: QuestionInputProps) => {
  const { answer } = question.answer || {};
  const [isEditing, setIsEditing] = useState(false);

  const userState = useAppSelector(state => state.user);
  const isAdmin = userState.user?.role !== 'user';

  const defaultValues = useMemo(() => {
    if (question.type === QuestionType.ADDRESS) {
      return {
        streetAddress: answer?.streetAddress,
        city: answer?.city,
        state: answer?.state,
        zip: answer?.zip,
      };
    } else if (question.type === QuestionType.SSN_EIN) {
      return {
        type: answer?.type || QuestionType.SSN,
        ssn: answer?.ssn,
        ein: answer?.ein,
        financialInstitution: answer?.financialInstitution,
      };
    } else {
      const value: any = { test: '' };
      value.test = formatDefaultValue(question.type, answer);
      return value;
    }
  }, []);

  const initialSchema = useMemo(() => {
    const questions = getQuestions(question, defaultValues);

    if (question.type === QuestionType.SSN_EIN) {
      questions.push({ name: 'type', type: InputType.INPUT });
    }

    return getSchema(questions.map(({ name, type }) => ({ name, type })));
  }, []);

  const [schema, setSchema] = useState(initialSchema);
  const methods = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const ssnEinType = methods.watch('type');

  const questions = useMemo(() => {
    return getQuestions(question, methods.getValues());
  }, [question, ssnEinType]);

  useEffect(() => {
    if (!!ssnEinType) {
      const newSchema = getSchema([
        ...questions.map(({ name, type }) => ({ name, type })),
        { name: 'type', type: InputType.INPUT },
      ]);

      setSchema(newSchema);
    }
  }, [ssnEinType]);

  const headerTitle =
    question.type === QuestionType.SSN_EIN ? methods.getValues('type') : title;

  const handleSubmit = (form: any) => {
    if (
      question.type === QuestionType.ADDRESS ||
      question.type === QuestionType.SSN_EIN
    ) {
      onSubmit(form);
    } else {
      onSubmit(form.test);
    }
    setIsEditing(false);
  };

  const handleClose = () => {
    setIsEditing(false);
    methods.reset();
    methods.clearErrors();
  };

  const changeSsnEinType = () => {
    const type = methods.getValues('type');

    if (type === InputType.SSN) {
      methods.setValue('type', InputType.EIN);
    } else {
      methods.setValue('type', InputType.SSN);
    }
  };

  const Header = useMemo(() => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <ContentCard title={headerTitle} />
        {question.type === QuestionType.SSN_EIN && isEditing && (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#3293DB',
              textDecoration: 'underline',
              whiteSpace: 'nowrap',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            onClick={changeSsnEinType}
          >
            Use{' '}
            {methods.getValues('type') === InputType.SSN
              ? InputType.EIN
              : InputType.SSN}
          </Typography>
        )}
      </Box>
    );
  }, [ssnEinType, isEditing]);

  const renderItem = (item: any, index?: number) => (
    <Box sx={{ maxWidth: '300px' }}>
      <DynamicInput
        key={index}
        title={item.title}
        name={item.name}
        type={item.type}
        values={item.values}
        placeholder={item.placeholder}
        disabled={!isEditing}
      />
    </Box>
  );

  const BottomComponent = useMemo(() => {
    if (!!isAdmin) {
      return undefined;
    }

    return isEditing ? (
      <Box sx={{ display: 'flex', gap: '5px' }}>
        <Button type='submit' sx={styles.button}>
          <Check sx={{ width: '17px' }} />
        </Button>
        <Button sx={styles.button} onClick={handleClose}>
          <Close sx={{ width: '17px' }} />
        </Button>
      </Box>
    ) : (
      <Button sx={styles.button} onClick={() => setIsEditing(true)}>
        <EditOutlined sx={{ width: '17px' }} />
      </Button>
    );
  }, [isEditing]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Dropdown
          itemsContainerStyle={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: '20px',
            marginTop: '12px',
          }}
          LeftHeaderComponent={
            <ProgressCircle progress={answer !== undefined ? 100 : 0} />
          }
          Header={Header}
          items={questions}
          renderItem={renderItem}
          BottomComponent={BottomComponent}
          border={false}
          boxShadow={boxShadow}
        />
        {!isLastQuestion && (
          <Box
            sx={{
              padding: '0 16px',
              background: 'white',
            }}
          >
            <Divider />
          </Box>
        )}
      </form>
    </FormProvider>
  );
};
